import { useQuery, useSuspenseQuery } from '@tanstack/react-query';
import { useParams } from 'react-router-dom';

import { accountLayoutQueryOptions } from '@lib/query/options/account';
import { orderDetailsQueryOptions } from '@lib/query/options/orders';
import PageStatusMessage from '@modules/order/components/page-status-message';
import OrderDetailsTemplate from '@modules/order/templates/order-details-template';

/**
 * Account Order Details Page
 *
 * Displays order details with payment notification for per-order settlement customers.
 * Uses aggregated BFF API (GET /api/orders/:id/details) for enriched data.
 *
 * accountLayoutQueryOptions 使用 useSuspenseQuery — 父層 AccountLayoutWrapper 已先行查詢並快取，
 * 此處直接讀取快取資料，與其他 account 子頁面一致。
 * orderDetailsQueryOptions 使用 useQuery — 訂單資料可能獨立失敗，需在此頁面自行處理 error/not-found。
 */
export default function AccountOrderDetailsPage() {
  const { id } = useParams<{ countryCode: string; id: string }>();

  const { data: accountData } = useSuspenseQuery(accountLayoutQueryOptions());
  const orderQuery = useQuery({
    ...orderDetailsQueryOptions(id ?? ''),
    enabled: !!id,
  });

  // 載入中：顯示內容區域的 skeleton（sidebar 由父層 AccountLayoutWrapper 渲染）
  if (orderQuery.isPending) {
    return (
      <div className="animate-pulse space-y-4" data-testid="order-details-loading">
        <div className="h-8 w-48 rounded bg-gray-200" />
        <div className="h-4 w-full max-w-md rounded bg-gray-200" />
        <div className="h-4 w-full max-w-sm rounded bg-gray-200" />
      </div>
    );
  }

  if (orderQuery.isError) {
    return (
      <PageStatusMessage
        data-testid="order-details-error"
        title="無法載入訂單資料"
        description="請稍後再試"
        onRetry={() => orderQuery.refetch()}
      />
    );
  }

  if (!accountData.customer || !orderQuery.data?.order) {
    return (
      <PageStatusMessage
        data-testid="order-details-not-found"
        title="找不到訂單資料"
        description="訂單資料不存在或已被移除"
        onRetry={() => orderQuery.refetch()}
      />
    );
  }

  const orderDetails = orderQuery.data;

  return (
    <div className="w-full" data-testid="order-details-page-wrapper">
      <OrderDetailsTemplate
        order={orderDetails.order}
        isPerOrderSettlement={orderDetails.is_per_order_settlement}
      />
    </div>
  );
}
